import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CreateExpenseCategoryDialog,
  type ExpenseCategory,
} from '@/components/spending/create-expense-category-dialog';
import { Listbox, type ListboxOption } from '@/components/ui/listbox';
import type { PopoverPortalTarget } from '@/components/ui/popover-portal';
import { api } from '@/lib/api';
import { hasPermission, PERMISSIONS } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';

const CREATE_VALUE = '__create_expense_category__';

export interface ExpenseCategorySelectProps {
  value: string | null;
  onChange: (categoryId: string | null, category?: ExpenseCategory | null) => void;
  popoverPortal?: PopoverPortalTarget;
  disabled?: boolean;
  id?: string;
  'aria-label'?: string;
}

/**
 * Expense category picker with an inline "New category" path so recording
 * spending never dead-ends on an empty dropdown.
 */
export const ExpenseCategorySelect = ({
  value,
  onChange,
  popoverPortal = 'body',
  disabled = false,
  id,
  'aria-label': ariaLabel,
}: ExpenseCategorySelectProps) => {
  const { t } = useTranslation('spending');
  const { t: tTips } = useTranslation('tips');
  const membership = useChurchStore((state) => state.membership);

  const canManage = hasPermission(membership, PERMISSIONS.EXPENSES_MANAGE_CATEGORIES);

  const [createOpen, setCreateOpen] = useState(false);

  const categories = useQuery({
    queryKey: ['expenses', 'categories'],
    queryFn: () => api.get<{ data: ExpenseCategory[] }>('/expenses/categories'),
  });

  const options = useMemo((): ListboxOption[] => {
    const rows = (categories.data?.data ?? [])
      .filter((row) => row.isActive !== false)
      .map((row) => ({
        value: row.id,
        label: row.name,
      }));
    if (!canManage) return rows;
    return [
      ...rows,
      {
        value: CREATE_VALUE,
        label: t('categories.createInline'),
        description: tTips('spending.categoryCreateInline'),
        icon: <Plus className="size-4" aria-hidden />,
      },
    ];
  }, [canManage, categories.data?.data, t, tTips]);

  return (
    <>
      <Listbox
        id={id}
        value={value}
        onChange={(next) => {
          if (next === CREATE_VALUE) {
            setCreateOpen(true);
            return;
          }
          const category = categories.data?.data.find((row) => row.id === next) ?? null;
          onChange(next, category);
        }}
        options={options}
        placeholder={t('categories.placeholder')}
        emptyMessage={t('categories.emptyPicker')}
        popoverPortal={popoverPortal}
        disabled={disabled || categories.isLoading}
        aria-label={ariaLabel}
      />

      <CreateExpenseCategoryDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(category) => onChange(category.id, category)}
      />
    </>
  );
};
